import React from 'react';

/**
 * Renders a message body with @mentions converted into clickable spans that
 * navigate to the mentioned user's profile.
 *
 * Usage:
 *   {renderMentions(message.content, navigate)}
 *
 * - Detects `@username` patterns (3-20 [a-z0-9_]).
 * - Wraps them in <span> with `data-mention` so they can be styled.
 * - Click navigates to `/profile/@<username>` (or `/u/<username>`).
 * - URL pattern is consumer-controlled via the optional `onMentionClick`
 *   callback; default uses react-router navigate('/profile/@u').
 *
 * Note: no autocomplete here — see `MentionInput` for that.
 */
const MENTION_REGEX = /(^|[^\w])@([a-z0-9_]{3,20})\b/gi;

export function renderMentions(text, onMentionClick) {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  // We need to reset the regex.lastIndex because /g regexes are stateful.
  MENTION_REGEX.lastIndex = 0;
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const pre = match[1] || '';
    const username = match[2].toLowerCase();
    const start = match.index + pre.length;
    // Push everything before the mention (including the preceding char that
    // was captured to anchor the regex).
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    parts.push(
      <span
        key={`m-${key++}`}
        className="text-violet-300 font-medium hover:underline cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          if (onMentionClick) onMentionClick(username);
        }}
        data-testid={`mention-${username}`}
      >
        @{username}
      </span>
    );
    lastIndex = start + 1 + username.length; // 1 for '@'
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : text;
}

/**
 * Returns the unique list of @usernames mentioned in `text` (no leading @).
 */
export function extractMentionUsernames(text) {
  if (!text || typeof text !== 'string') return [];
  const set = new Set();
  let match;
  MENTION_REGEX.lastIndex = 0;
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    set.add(match[2].toLowerCase());
  }
  return Array.from(set);
}
